/**
 * Error handling utilities for SumoPod AI integration
 */

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  retryable?: boolean;
}

/**
 * Parse and categorize API errors
 */
export const parseApiError = (error: unknown): ApiError => {
  // Network errors
  if (error.name === "TypeError" && error.message.includes("fetch")) {
    return {
      message:
        "Tidak dapat terhubung ke server. Periksa koneksi internet Anda.",
      code: "NETWORK_ERROR",
      retryable: true,
    };
  }

  // HTTP errors
  if (error.status) {
    switch (error.status) {
      case 400:
        return {
          message:
            "Data yang dikirim tidak valid. Mohon periksa kembali form Anda.",
          code: "BAD_REQUEST",
          status: 400,
          retryable: false,
        };
      case 401:
        return {
          message: "Sesi Anda telah berakhir. Silakan login kembali.",
          code: "UNAUTHORIZED",
          status: 401,
          retryable: false,
        };
      case 403:
        return {
          message: "Anda tidak memiliki akses untuk melakukan tindakan ini.",
          code: "FORBIDDEN",
          status: 403,
          retryable: false,
        };
      case 429:
        return {
          message:
            "Terlalu banyak permintaan. Silakan tunggu sebentar dan coba lagi.",
          code: "RATE_LIMITED",
          status: 429,
          retryable: true,
        };
      case 500:
        return {
          message:
            "Terjadi kesalahan pada server. Tim kami sedang memperbaikinya.",
          code: "SERVER_ERROR",
          status: 500,
          retryable: true,
        };
      case 503:
        return {
          message:
            "Layanan sedang dalam pemeliharaan. Silakan coba lagi nanti.",
          code: "SERVICE_UNAVAILABLE",
          status: 503,
          retryable: true,
        };
      default:
        return {
          message: `Terjadi kesalahan HTTP ${error.status}. Silakan coba lagi.`,
          code: "HTTP_ERROR",
          status: error.status,
          retryable: true,
        };
    }
  }

  // SumoPod AI API specific errors
  if (error.message?.includes("API key")) {
    return {
      message: "Konfigurasi API tidak valid. Silakan hubungi administrator.",
      code: "API_KEY_ERROR",
      retryable: false,
    };
  }

  if (error.message?.includes("quota") || error.message?.includes("limit")) {
    return {
      message: "Kuota API telah habis. Silakan coba lagi nanti.",
      code: "QUOTA_EXCEEDED",
      retryable: true,
    };
  }

  if (error.message?.includes("timeout")) {
    return {
      message: "Permintaan memakan waktu terlalu lama. Silakan coba lagi.",
      code: "TIMEOUT",
      retryable: true,
    };
  }

  // Generic error
  return {
    message:
      error.message ||
      "Terjadi kesalahan yang tidak diketahui. Silakan coba lagi.",
    code: "UNKNOWN_ERROR",
    retryable: true,
  };
};

/**
 * Retry logic for API calls
 */
export const withRetry = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      const apiError = parseApiError(error);

      // Don't retry if error is not retryable
      if (!apiError.retryable || attempt === maxRetries) {
        throw error;
      }

      // Wait before retrying with exponential backoff
      const waitTime = delay * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }

  throw lastError;
};

/**
 * Validate environment configuration
 */
export const validateEnvironment = (): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!import.meta.env.VITE_SUPABASE_URL) {
    errors.push("VITE_SUPABASE_URL is not configured");
  }

  if (!import.meta.env.VITE_SUPABASE_ANON_KEY) {
    errors.push("VITE_SUPABASE_ANON_KEY is not configured");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Log errors for debugging
 */
export const logError = (
  context: string,
  error: unknown,
  additionalData?: unknown
) => {
  console.group(`💥 Error in ${context}`);
  console.error("Error:", error);
  if (additionalData) {
    console.error("Additional data:", additionalData);
  }
  console.error("Stack trace:", error.stack);
  console.groupEnd();
};

/**
 * Create user-friendly error messages
 */
export const createUserMessage = (error: ApiError): string => {
  const baseMessage = error.message;

  if (error.retryable) {
    return `${baseMessage} Jika masalah berlanjut, silakan hubungi dukungan teknis.`;
  }

  return baseMessage;
};

/**
 * Check if error is a network connectivity issue
 */
export const isNetworkError = (error: unknown): boolean => {
  return (
    error.name === "TypeError" ||
    error.code === "NETWORK_ERROR" ||
    error.message?.includes("fetch") ||
    error.message?.includes("network")
  );
};

/**
 * Check if error suggests the user should retry
 */
export const shouldRetry = (error: unknown): boolean => {
  const apiError = parseApiError(error);
  return apiError.retryable || false;
};

/**
 * Format error for display to users
 */
export const formatErrorForUser = (
  error: unknown,
  context: string = "operasi"
): string => {
  const apiError = parseApiError(error);

  if (isNetworkError(error)) {
    return `Tidak dapat terhubung ke server. Periksa koneksi internet Anda dan coba lagi.`;
  }

  if (apiError.code === "RATE_LIMITED") {
    return `Terlalu banyak permintaan. Silakan tunggu sebentar sebelum mencoba lagi.`;
  }

  if (apiError.code === "SERVER_ERROR") {
    return `Terjadi kesalahan pada server. Tim kami sedang memperbaikinya.`;
  }

  return `Gagal melakukan ${context}. ${apiError.message}`;
};
